// Mesai Takip Context
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { WorkTimeSession, WorkTimeSummary, WorkTimeStatus, WorkTimeType } from '../types';
import workTimeService from '../services/workTimeService';
import dataConnectionService, { WorkTimeEntry as DataWorkTimeEntry } from '../services/dataConnectionService';
import { useAuth } from './AuthContext';

interface WorkTimeContextType {
  currentSession: WorkTimeSession | null;
  workTimeStatus: WorkTimeStatus;
  isLoading: boolean;
  
  // Mesai işlemleri
  startWork: (notes?: string, location?: string) => Promise<boolean>;
  endWork: (notes?: string) => Promise<boolean>;
  startBreak: (notes?: string) => Promise<boolean>;
  endBreak: (notes?: string) => Promise<boolean>;
  
  // Veri alma
  getDailySummary: (date: string) => Promise<WorkTimeSummary | null>;
  getWorkTimeHistory: (startDate: string, endDate: string) => Promise<WorkTimeSummary[]>;
  getCurrentSession: () => Promise<WorkTimeSession | null>;
  
  // Yardımcı fonksiyonlar
  formatDuration: (minutes: number) => string;
  getStatusText: (status: WorkTimeStatus) => string;
  getStatusColor: (status: WorkTimeStatus) => string;
}

const WorkTimeContext = createContext<WorkTimeContextType | undefined>(undefined);

interface WorkTimeProviderProps {
  children: ReactNode;
}

export const WorkTimeProvider: React.FC<WorkTimeProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [currentSession, setCurrentSession] = useState<WorkTimeSession | null>(null);
  const [workTimeStatus, setWorkTimeStatus] = useState<WorkTimeStatus>(WorkTimeStatus.OFF_DUTY);
  const [isLoading, setIsLoading] = useState(false);

  // Kullanıcı değiştiğinde mevcut session'ı yükle
  useEffect(() => {
    if (user) {
      loadCurrentSession();
    } else {
      setCurrentSession(null);
      setWorkTimeStatus(WorkTimeStatus.OFF_DUTY);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Mevcut session'ı yükle
  const loadCurrentSession = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const result = await workTimeService.getCurrentSession(user.id);
      if (result.success && result.data) {
        setCurrentSession(result.data);
        setWorkTimeStatus(result.data.status);
      } else {
        setCurrentSession(null);
        setWorkTimeStatus(WorkTimeStatus.OFF_DUTY);
      }
    } catch (error) {
      console.error('Session yükleme hatası:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Mesai başlat
  const startWork = async (notes?: string, location?: string): Promise<boolean> => {
    if (!user) return false;
    
    setIsLoading(true);
    try {
      // Mesai girişi kaydet
      const entryResult = await workTimeService.recordWorkTimeEntry(
        user.id, 
        WorkTimeType.WORK_START, 
        notes, 
        location
      );
      
      if (!entryResult.success) {
        console.error('Mesai girişi kaydedilemedi:', entryResult.error);
        return false;
      }

      // Gerçek data connection'a kaydet
      const dataEntry: DataWorkTimeEntry = {
        id: `work_start_${Date.now()}`,
        userId: user.id,
        type: 'work_start',
        timestamp: new Date(),
        notes,
        location
      };
      await dataConnectionService.saveWorkTimeEntry(dataEntry);
      
      // Log kaydı oluştur
      await dataConnectionService.logUserActivity(user.id, 'work_start', {
        notes,
        location,
        timestamp: new Date().toISOString()
      });

      // Session durumunu güncelle
      const sessionResult = await workTimeService.updateWorkTimeStatus(
        user.id, 
        WorkTimeStatus.WORKING,
        { startTime: new Date() }
      );
      
      if (sessionResult.success && sessionResult.data) {
        setCurrentSession(sessionResult.data);
        setWorkTimeStatus(WorkTimeStatus.WORKING);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Mesai başlatma hatası:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Mesai bitir
  const endWork = async (notes?: string): Promise<boolean> => {
    if (!user) return false;
    
    setIsLoading(true);
    try {
      // Mesai çıkışı kaydet
      const entryResult = await workTimeService.recordWorkTimeEntry(
        user.id, 
        WorkTimeType.WORK_END, 
        notes
      );
      
      if (!entryResult.success) {
        console.error('Mesai çıkışı kaydedilemedi:', entryResult.error);
        return false;
      }

      // Gerçek data connection'a kaydet
      const dataEntry: DataWorkTimeEntry = {
        id: `work_end_${Date.now()}`,
        userId: user.id,
        type: 'work_end',
        timestamp: new Date(),
        notes
      };
      await dataConnectionService.saveWorkTimeEntry(dataEntry);
      
      // Log kaydı oluştur
      await dataConnectionService.logUserActivity(user.id, 'work_end', {
        notes,
        timestamp: new Date().toISOString()
      });

      // Session durumunu güncelle
      const sessionResult = await workTimeService.updateWorkTimeStatus(
        user.id, 
        WorkTimeStatus.OFF_DUTY,
        { endTime: new Date() }
      );
      
      if (sessionResult.success) {
        setCurrentSession(null);
        setWorkTimeStatus(WorkTimeStatus.OFF_DUTY);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Mesai bitirme hatası:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Mola başlat
  const startBreak = async (notes?: string): Promise<boolean> => {
    if (!user) return false;
    
    setIsLoading(true);
    try {
      // Mola girişi kaydet
      const entryResult = await workTimeService.recordWorkTimeEntry(
        user.id, 
        WorkTimeType.BREAK_START, 
        notes
      );
      
      if (!entryResult.success) {
        console.error('Mola girişi kaydedilemedi:', entryResult.error);
        return false;
      }

      // Gerçek data connection'a kaydet
      const dataEntry: DataWorkTimeEntry = {
        id: `break_start_${Date.now()}`,
        userId: user.id,
        type: 'break_start',
        timestamp: new Date(),
        notes
      };
      await dataConnectionService.saveWorkTimeEntry(dataEntry);
      
      // Log kaydı oluştur
      await dataConnectionService.logUserActivity(user.id, 'break_start', {
        notes,
        timestamp: new Date().toISOString()
      });

      // Session durumunu güncelle
      const sessionResult = await workTimeService.updateWorkTimeStatus(
        user.id, 
        WorkTimeStatus.ON_BREAK,
        { breakStartTime: new Date() }
      );
      
      if (sessionResult.success && sessionResult.data) {
        setCurrentSession(sessionResult.data);
        setWorkTimeStatus(WorkTimeStatus.ON_BREAK);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Mola başlatma hatası:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Mola bitir
  const endBreak = async (notes?: string): Promise<boolean> => {
    if (!user) return false;
    
    setIsLoading(true);
    try {
      // Mola çıkışı kaydet
      const entryResult = await workTimeService.recordWorkTimeEntry(
        user.id, 
        WorkTimeType.BREAK_END, 
        notes
      );
      
      if (!entryResult.success) {
        console.error('Mola çıkışı kaydedilemedi:', entryResult.error);
        return false;
      }

      // Gerçek data connection'a kaydet
      const dataEntry: DataWorkTimeEntry = {
        id: `break_end_${Date.now()}`,
        userId: user.id,
        type: 'break_end',
        timestamp: new Date(),
        notes
      };
      await dataConnectionService.saveWorkTimeEntry(dataEntry);
      
      // Log kaydı oluştur
      await dataConnectionService.logUserActivity(user.id, 'break_end', {
        notes,
        timestamp: new Date().toISOString()
      });

      // Session durumunu güncelle
      const sessionResult = await workTimeService.updateWorkTimeStatus(
        user.id, 
        WorkTimeStatus.WORKING,
        { breakEndTime: new Date() }
      );
      
      if (sessionResult.success && sessionResult.data) {
        setCurrentSession(sessionResult.data);
        setWorkTimeStatus(WorkTimeStatus.WORKING);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Mola bitirme hatası:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Günlük özet al
  const getDailySummary = async (date: string): Promise<WorkTimeSummary | null> => {
    if (!user) return null;
    
    try {
      const result = await workTimeService.getDailySummary(user.id, date);
      return result.success ? result.data || null : null;
    } catch (error) {
      console.error('Günlük özet alma hatası:', error);
      return null;
    }
  };

  // Mesai geçmişi al
  const getWorkTimeHistory = async (startDate: string, endDate: string): Promise<WorkTimeSummary[]> => {
    if (!user) return [];
    
    try {
      const result = await workTimeService.getWorkTimeHistory(user.id, startDate, endDate);
      return result.success ? result.data || [] : [];
    } catch (error) {
      console.error('Mesai geçmişi alma hatası:', error);
      return [];
    }
  };

  // Mevcut session'ı al
  const getCurrentSession = async (): Promise<WorkTimeSession | null> => {
    if (!user) return null;
    
    try {
      const result = await workTimeService.getCurrentSession(user.id);
      return result.success ? result.data || null : null;
    } catch (error) {
      console.error('Session alma hatası:', error);
      return null;
    }
  };

  // Süre formatla
  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Durum metni
  const getStatusText = (status: WorkTimeStatus): string => {
    switch (status) {
      case WorkTimeStatus.WORKING:
        return 'Çalışıyor';
      case WorkTimeStatus.ON_BREAK:
        return 'Molada';
      case WorkTimeStatus.OFF_DUTY:
        return 'Mesai Dışı';
      default:
        return 'Bilinmeyen';
    }
  };

  // Durum rengi
  const getStatusColor = (status: WorkTimeStatus): string => {
    switch (status) {
      case WorkTimeStatus.WORKING:
        return '#4caf50'; // Yeşil
      case WorkTimeStatus.ON_BREAK:
        return '#ff9800'; // Turuncu
      case WorkTimeStatus.OFF_DUTY:
        return '#f44336'; // Kırmızı
      default:
        return '#9e9e9e'; // Gri
    }
  };

  const value: WorkTimeContextType = {
    currentSession,
    workTimeStatus,
    isLoading,
    startWork,
    endWork,
    startBreak,
    endBreak,
    getDailySummary,
    getWorkTimeHistory,
    getCurrentSession,
    formatDuration,
    getStatusText,
    getStatusColor
  };

  return (
    <WorkTimeContext.Provider value={value}>
      {children}
    </WorkTimeContext.Provider>
  );
};

export const useWorkTime = (): WorkTimeContextType => {
  const context = useContext(WorkTimeContext);
  if (context === undefined) {
    throw new Error('useWorkTime must be used within a WorkTimeProvider');
  }
  return context;
};
